import { Fragment } from 'react';
import Alert from '@mui/material/Alert';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import {
  AccountDetailSchema,
  AccountLedgerSchema,
  BalanceSheetSchema,
  PostingRejectionSchema,
  ProfitAndLossSchema,
  TrialBalanceSchema,
  type AccountDto,
  type StatementGroupDto,
} from '@taqon/contracts';
import { z } from 'zod';
import { getData } from '../../shared/api/client';
import { ExportButton } from '../../shared/export/ExportButton';
import { recordPath } from '../../shared/records/routes';
import { hasCapability, useSession } from '../../shared/session/session';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { RecordLink } from '../finance/detail';
import { businessToday } from '../finance/line-editor';
import { AccountPicker } from '../finance/lookups';
import { DimensionFilter, Money, RangeFilter, useReportParams, yearToDate } from './common';

const REPORTS = '/api/v1/ledger/reports';
const label = (value: string) => value.replace(/_/g, ' ');

/** The date range in the URL, defaulting to the business year to date. */
function useRange() {
  const session = useSession();
  const report = useReportParams();
  const fallback = yearToDate(businessToday(session.data?.businessTimezone));
  const from = report.get('from') || fallback.from;
  const to = report.get('to') || fallback.to;
  return { report, from, to };
}

/** A link to the account ledger behind a figure, with the report's range and dimensions. */
function ledgerHref(
  report: ReturnType<typeof useReportParams>,
  accountId: string,
  range: { from: string; to: string },
): string {
  const query = report.query([]);
  query.set('accountId', accountId);
  query.set('from', range.from);
  query.set('to', range.to);
  return `/reports/account-ledger?${query.toString()}`;
}

/**
 * Trial Balance (FRD-FIN-053, FIN-004): opening, period and closing balances by account, or rolled
 * up to an L1–L4 group, for a date range and dimension combination. Each account drills down to
 * its account ledger (FRD-FIN-137).
 */
export function TrialBalancePage() {
  const { report, from, to } = useRange();
  const level = report.get('level') || 'L5';
  const includeZero = report.get('includeZero') === 'true';
  const query = report.query(['includeZero']);
  query.set('from', from);
  query.set('to', to);
  query.set('level', level);
  const tb = useQuery({
    queryKey: ['report', 'trial-balance', query.toString()],
    queryFn: () => getData(`${REPORTS}/trial-balance?${query.toString()}`, TrialBalanceSchema),
  });
  return (
    <Stack spacing={3}>
      <PageHeader
        title="Trial balance"
        description="Every account's opening, period and closing balance, from posted journals."
        actions={
          <ExportButton
            recordType="ledger.journal"
            href={`${REPORTS}/trial-balance/export?${query.toString()}`}
          />
        }
      />
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        useFlexGap
        sx={{ flexWrap: 'wrap' }}
      >
        <RangeFilter
          from={from}
          to={to}
          onChange={(range) => {
            report.set(range);
          }}
        />
        <TextField
          select
          size="small"
          label="Level"
          value={level}
          sx={{ minWidth: 160 }}
          onChange={(e) => {
            report.set({ level: e.target.value });
          }}
        >
          <MenuItem value="L5">Accounts (L5)</MenuItem>
          <MenuItem value="L4">L4 groups</MenuItem>
          <MenuItem value="L3">L3 groups</MenuItem>
          <MenuItem value="L2">L2 groups</MenuItem>
          <MenuItem value="L1">L1 classes</MenuItem>
        </TextField>
        <FormControlLabel
          control={
            <Switch
              checked={includeZero}
              onChange={(e) => {
                report.set({ includeZero: e.target.checked ? 'true' : '' });
              }}
            />
          }
          label="Accounts with no postings"
        />
      </Stack>
      <DimensionFilter />
      {tb.isPending && <LoadingState label="Loading the trial balance" />}
      {tb.isError && <ErrorState message={tb.error.message} />}
      {tb.data !== undefined && (
        <Section title={`${tb.data.from} to ${tb.data.to}`}>
          {!tb.data.balanced && (
            <Alert severity="error">
              The trial balance does not balance. Report this to Finance.
            </Alert>
          )}
          {tb.data.rows.length === 0 ? (
            <EmptyState title="Nothing posted" detail="No posted journal falls in this range." />
          ) : (
            <Table size="small" aria-label="Trial balance">
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell align="right">Opening Dr</TableCell>
                  <TableCell align="right">Opening Cr</TableCell>
                  <TableCell align="right">Period Dr</TableCell>
                  <TableCell align="right">Period Cr</TableCell>
                  <TableCell align="right">Closing Dr</TableCell>
                  <TableCell align="right">Closing Cr</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tb.data.rows.map((row) => (
                  <TableRow key={`${row.level}-${row.code}`} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {row.accountId !== null ? (
                        <RecordLink to={ledgerHref(report, row.accountId, { from, to })}>
                          {row.code}
                        </RecordLink>
                      ) : row.groupId !== null ? (
                        <RecordLink to={`/accounts?group=${row.groupId}`}>{row.code}</RecordLink>
                      ) : (
                        row.code
                      )}
                    </TableCell>
                    <TableCell>
                      {row.name}
                      <Typography variant="caption" component="div" color="text.secondary">
                        {row.path}
                      </Typography>
                    </TableCell>
                    <Money value={row.openingDebit} />
                    <Money value={row.openingCredit} />
                    <Money value={row.periodDebit} />
                    <Money value={row.periodCredit} />
                    <Money value={row.closingDebit} />
                    <Money value={row.closingCredit} />
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={2} sx={{ fontWeight: 700 }}>
                    Total
                  </TableCell>
                  <Money value={tb.data.totals.openingDebit} bold />
                  <Money value={tb.data.totals.openingCredit} bold />
                  <Money value={tb.data.totals.periodDebit} bold />
                  <Money value={tb.data.totals.periodCredit} bold />
                  <Money value={tb.data.totals.closingDebit} bold />
                  <Money value={tb.data.totals.closingCredit} bold />
                </TableRow>
              </TableBody>
            </Table>
          )}
        </Section>
      )}
    </Stack>
  );
}

/**
 * Account Ledger (FRD-FIN-053): one account's posted lines in a range, oldest first, with the
 * running balance; each line opens its journal and, where the source has a page, its source
 * transaction (FRD-FIN-048, FIN-137).
 */
export function AccountLedgerPage() {
  const { report, from, to } = useRange();
  const accountId = report.get('accountId');
  const account = useQuery({
    queryKey: ['account', accountId],
    enabled: accountId !== '',
    queryFn: () => getData(`/api/v1/ledger/accounts/${accountId}`, AccountDetailSchema),
  });
  const query = report.query(['accountId']);
  query.set('from', from);
  query.set('to', to);
  const ledger = useQuery({
    queryKey: ['report', 'account-ledger', query.toString()],
    enabled: accountId !== '',
    queryFn: () => getData(`${REPORTS}/account-ledger?${query.toString()}`, AccountLedgerSchema),
  });
  return (
    <Stack spacing={3}>
      <PageHeader
        title="Account ledger"
        description="The posted transactions that make up an account's balance."
        actions={
          accountId === '' ? undefined : (
            <ExportButton
              recordType="ledger.journal"
              href={`${REPORTS}/account-ledger/export?${query.toString()}`}
            />
          )
        }
      />
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        useFlexGap
        sx={{ flexWrap: 'wrap' }}
      >
        <Stack sx={{ minWidth: 320 }}>
          <AccountPicker
            value={account.data?.account ?? null}
            onChange={(value: AccountDto | null) => {
              report.set({ accountId: value?.id ?? '' });
            }}
          />
        </Stack>
        <RangeFilter
          from={from}
          to={to}
          onChange={(range) => {
            report.set(range);
          }}
        />
      </Stack>
      <DimensionFilter />
      {accountId === '' && (
        <Section>
          <EmptyState title="Choose an account" />
        </Section>
      )}
      {ledger.isPending && accountId !== '' && <LoadingState label="Loading the account ledger" />}
      {ledger.isError && <ErrorState message={ledger.error.message} />}
      {ledger.data !== undefined && (
        <Section
          title={`${ledger.data.account.code} — ${ledger.data.account.name} (${ledger.data.from} to ${ledger.data.to})`}
        >
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, pt: 1 }}>
            {ledger.data.account.path} · normal balance {ledger.data.account.normalBalance}
          </Typography>
          {ledger.data.truncated && (
            <Alert severity="warning">
              More lines than one report shows: narrow the dates to see them all.
            </Alert>
          )}
          <Table size="small" aria-label="Account ledger">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Journal</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Debit</TableCell>
                <TableCell align="right">Credit</TableCell>
                <TableCell align="right">Balance (Dr − Cr)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell colSpan={7}>Opening balance</TableCell>
                <Money value={ledger.data.openingBalance} bold />
              </TableRow>
              {ledger.data.lines.map((line) => {
                const source =
                  line.sourceRecordType === null || line.sourceRecordId === null
                    ? null
                    : recordPath(line.sourceRecordType, line.sourceRecordId);
                return (
                  <TableRow key={`${line.journalId}-${String(line.lineNo)}`} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{line.postingDate}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <RecordLink to={`/journals/${line.journalId}`}>{line.documentNo}</RecordLink>
                    </TableCell>
                    <TableCell>
                      {source === null ? (
                        label(line.sourceType)
                      ) : (
                        <RecordLink to={source}>{label(line.sourceType)}</RecordLink>
                      )}
                    </TableCell>
                    <TableCell>{line.reference ?? '—'}</TableCell>
                    <TableCell>{line.description ?? '—'}</TableCell>
                    <Money value={line.debit} />
                    <Money value={line.credit} />
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {line.runningBalance}
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow>
                <TableCell colSpan={5} sx={{ fontWeight: 700 }}>
                  Totals and closing balance
                </TableCell>
                <Money value={ledger.data.totalDebit} bold />
                <Money value={ledger.data.totalCredit} bold />
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {ledger.data.closingBalance}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Section>
      )}
    </Stack>
  );
}

/** A statement tree: each group with its accounts and subgroups, and the group's total. */
function StatementRows({
  groups,
  depth,
  href,
}: {
  groups: readonly StatementGroupDto[];
  depth: number;
  href: (accountId: string) => string;
}) {
  return (
    <>
      {groups.map((group) => (
        <Fragment key={group.groupId}>
          <TableRow>
            <TableCell sx={{ pl: 2 + depth * 2, fontWeight: 600 }} colSpan={2}>
              {group.code} {group.name}
            </TableCell>
          </TableRow>
          {group.accounts.map((account) => (
            <TableRow key={account.accountId} hover>
              <TableCell sx={{ pl: 4 + depth * 2 }}>
                <RecordLink to={href(account.accountId)}>{account.code}</RecordLink> {account.name}
                {account.contra && (
                  <Typography component="span" variant="caption" color="text.secondary">
                    {' '}
                    (contra — deducted)
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                {account.contra ? `(${account.amount})` : account.amount}
              </TableCell>
            </TableRow>
          ))}
          <StatementRows groups={group.groups} depth={depth + 1} href={href} />
          <TableRow>
            <TableCell sx={{ pl: 2 + depth * 2, fontWeight: 700 }}>Total {group.name}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
              {group.amount}
            </TableCell>
          </TableRow>
        </Fragment>
      ))}
    </>
  );
}

/**
 * Profit & Loss (FRD-FIN-129) from the approved hierarchy for a range and dimension combination:
 * each account on its normal-balance side (FRD-FIN-013), contra accounts deducted (FRD-FIN-014),
 * groups totalled (FRD-FIN-004), and every account opening its ledger (FRD-FIN-137).
 */
export function ProfitAndLossPage() {
  const { report, from, to } = useRange();
  const query = report.query(['includeZero']);
  query.set('from', from);
  query.set('to', to);
  const pl = useQuery({
    queryKey: ['report', 'profit-and-loss', query.toString()],
    queryFn: () => getData(`${REPORTS}/profit-and-loss?${query.toString()}`, ProfitAndLossSchema),
  });
  return (
    <Stack spacing={3}>
      <PageHeader
        title="Profit & loss"
        description="Income and expenses from the Chart of Accounts hierarchy, for the dates and dimensions chosen."
        actions={
          <ExportButton
            recordType="ledger.journal"
            href={`${REPORTS}/profit-and-loss/export?${query.toString()}`}
          />
        }
      />
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        useFlexGap
        sx={{ flexWrap: 'wrap' }}
      >
        <RangeFilter
          from={from}
          to={to}
          onChange={(range) => {
            report.set(range);
          }}
        />
      </Stack>
      <DimensionFilter />
      {pl.isPending && <LoadingState label="Loading the profit and loss" />}
      {pl.isError && <ErrorState message={pl.error.message} />}
      {pl.data !== undefined && (
        <Section title={`${pl.data.from} to ${pl.data.to}`}>
          {pl.data.classes.length === 0 ? (
            <EmptyState title="Nothing posted" detail="No income or expense falls in this range." />
          ) : (
            <Table size="small" aria-label="Profit and loss">
              <TableBody>
                <StatementRows
                  groups={pl.data.classes}
                  depth={0}
                  href={(accountId) => ledgerHref(report, accountId, { from, to })}
                />
              </TableBody>
            </Table>
          )}
          <Table size="small" aria-label="Net result">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Net profit / (loss)</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {pl.data.netProfit}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Section>
      )}
    </Stack>
  );
}

/**
 * Balance Sheet as at a date (FRD-FIN-130): the balance-sheet accounts through the hierarchy, and
 * the P&L result not yet closed to equity (year-end close is a later capability, FRD-FIN-114).
 */
export function BalanceSheetPage() {
  const session = useSession();
  const report = useReportParams();
  const today = businessToday(session.data?.businessTimezone);
  const asAt = report.get('asAt') || today;
  const query = report.query(['includeZero']);
  query.set('asAt', asAt);
  const bs = useQuery({
    queryKey: ['report', 'balance-sheet', query.toString()],
    queryFn: () => getData(`${REPORTS}/balance-sheet?${query.toString()}`, BalanceSheetSchema),
  });
  const yearStart = `${asAt.slice(0, 4)}-01-01`;
  return (
    <Stack spacing={3}>
      <PageHeader
        title="Balance sheet"
        description="The position as at a date, from the Chart of Accounts hierarchy."
        actions={
          <ExportButton
            recordType="ledger.journal"
            href={`${REPORTS}/balance-sheet/export?${query.toString()}`}
          />
        }
      />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <TextField
          size="small"
          type="date"
          label="As at"
          value={asAt}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(e) => {
            report.set({ asAt: e.target.value });
          }}
        />
      </Stack>
      <DimensionFilter />
      {bs.isPending && <LoadingState label="Loading the balance sheet" />}
      {bs.isError && <ErrorState message={bs.error.message} />}
      {bs.data !== undefined && (
        <Section title={`As at ${bs.data.asAt}`}>
          {!bs.data.balanced && (
            <Alert severity="error">
              The balance sheet does not balance. Report this to Finance.
            </Alert>
          )}
          {bs.data.classes.length === 0 ? (
            <EmptyState title="Nothing posted" detail="No balance-sheet account has a balance." />
          ) : (
            <Table size="small" aria-label="Balance sheet">
              <TableBody>
                <StatementRows
                  groups={bs.data.classes}
                  depth={0}
                  href={(accountId) => ledgerHref(report, accountId, { from: yearStart, to: asAt })}
                />
              </TableBody>
            </Table>
          )}
          <Table size="small" aria-label="Balance sheet totals">
            <TableBody>
              <TableRow>
                <TableCell>Profit or loss not yet closed to equity</TableCell>
                <TableCell align="right">{bs.data.unclosedProfit}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Total of debit balances</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {bs.data.totalDebitSide}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>
                  Total of credit balances and unclosed result
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {bs.data.totalCreditSide}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Section>
      )}
    </Stack>
  );
}

/**
 * Posting validation report (FRD-FIN-038): every refused posting attempt whose intended date falls
 * in the range, with the failing line, account and rule (FRD-FIN-036), linked to what was attempted.
 */
export function PostingRejectionsPage() {
  const { report, from, to } = useRange();
  const session = useSession();
  const query = new URLSearchParams({ from, to });
  const rows = useQuery({
    queryKey: ['report', 'posting-rejections', query.toString()],
    queryFn: () =>
      getData(`${REPORTS}/posting-rejections?${query.toString()}`, z.array(PostingRejectionSchema)),
  });
  return (
    <Stack spacing={3}>
      <PageHeader
        title="Posting validation report"
        description="Refused posting attempts and the rules that refused them, by intended posting date."
      />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <RangeFilter
          from={from}
          to={to}
          onChange={(range) => {
            report.set(range);
          }}
        />
      </Stack>
      {rows.isPending && <LoadingState label="Loading refused postings" />}
      {rows.isError && <ErrorState message={rows.error.message} />}
      {rows.data !== undefined && (
        <Section title={`${from} to ${to}`}>
          {rows.data.length === 0 ? (
            <EmptyState title="No refused postings" />
          ) : (
            <Table size="small" aria-label="Refused postings">
              <TableHead>
                <TableRow>
                  <TableCell>Attempted</TableCell>
                  <TableCell>Intended date</TableCell>
                  <TableCell>Attempted for</TableCell>
                  <TableCell>Line</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Rule</TableCell>
                  <TableCell>Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.data.flatMap((row) =>
                  row.failures.map((failure, index) => {
                    const path =
                      row.sourceRecordType === null || row.sourceRecordId === null
                        ? null
                        : recordPath(row.sourceRecordType, row.sourceRecordId);
                    const may =
                      row.sourceRecordType !== null &&
                      hasCapability(session.data, `${row.sourceRecordType}:view`);
                    const text =
                      row.sourceDisplayName === null
                        ? 'Manual posting'
                        : `${row.sourceDisplayName} #${row.sourceRecordId ?? ''}`;
                    return (
                      <TableRow key={`${row.id}-${String(index)}`}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {row.attemptedAt.slice(0, 16).replace('T', ' ')}
                        </TableCell>
                        <TableCell>{row.intendedPostingDate}</TableCell>
                        <TableCell>
                          {path !== null && may ? <RecordLink to={path}>{text}</RecordLink> : text}
                        </TableCell>
                        <TableCell>{failure.lineNo === 0 ? '—' : failure.lineNo}</TableCell>
                        <TableCell>{failure.accountRef ?? '—'}</TableCell>
                        <TableCell>{label(failure.rule)}</TableCell>
                        <TableCell>{failure.message}</TableCell>
                      </TableRow>
                    );
                  }),
                )}
              </TableBody>
            </Table>
          )}
        </Section>
      )}
    </Stack>
  );
}
